import _AlertWarningFilledIcon from "@adsk/alloy-react-icon/es/lib/AlertWarningFilledIcon";
import _styled, { default as _styled2, default as _styled3, default as _styled4, default as _styled5, default as _styled6, default as _styled7 } from "styled-components";
var _templateObject, _templateObject2, _templateObject3, _templateObject4, _templateObject5, _templateObject6;
const _excluded = ["className", "style", "error", "errorVariant", "label", "labelVariant", "required", "children", "renderInfoPopover"];
function _taggedTemplateLiteral(e, t) { return t || (t = e.slice(0)), Object.freeze(Object.defineProperties(e, { raw: { value: Object.freeze(t) } })); }
function _extends() { return _extends = Object.assign ? Object.assign.bind() : function (n) { for (var e = 1; e < arguments.length; e++) { var t = arguments[e]; for (var r in t) ({}).hasOwnProperty.call(t, r) && (n[r] = t[r]); } return n; }, _extends.apply(null, arguments); }
function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
function _defineProperty(e, r, t) { return (r = _toPropertyKey(r)) in e ? Object.defineProperty(e, r, { value: t, enumerable: !0, configurable: !0, writable: !0 }) : e[r] = t, e; }
function _toPropertyKey(t) { var i = _toPrimitive(t, "string"); return "symbol" == typeof i ? i : i + ""; }
function _toPrimitive(t, r) { if ("object" != typeof t || !t) return t; var e = t[Symbol.toPrimitive]; if (void 0 !== e) { var i = e.call(t, r || "default"); if ("object" != typeof i) return i; throw new TypeError("@@toPrimitive must return a primitive value."); } return ("string" === r ? String : Number)(t); }
function _objectWithoutProperties(e, t) { if (null == e) return {}; var o, r, i = _objectWithoutPropertiesLoose(e, t); if (Object.getOwnPropertySymbols) { var s = Object.getOwnPropertySymbols(e); for (r = 0; r < s.length; r++) o = s[r], t.includes(o) || {}.propertyIsEnumerable.call(e, o) && (i[o] = e[o]); } return i; }
function _objectWithoutPropertiesLoose(r, e) { if (null == r) return {}; var t = {}; for (var n in r) if ({}.hasOwnProperty.call(r, n)) { if (e.includes(n)) continue; t[n] = r[n]; } return t; }
import React from 'react';
import PropTypes from 'prop-types';
import { useInstanceId, variantSwitch } from '@adsk/alloy-react-helpers';
import theme, { stylePropType } from '@adsk/alloy-react-theme';
import Tooltip, { InfoPopover } from '@adsk/alloy-react-tooltip';
const INFO_POPOVER_ICON_STYLE = {
  marginLeft: 5,
  height: 16
};
const FORM_FIELD_ERROR_VARIANTS = {
  CAPTION: 'caption',
  TOOLTIP: 'tooltip'
};
const DEFAULT_INFO_POPOVER_PROPS = {
  variant: InfoPopover.VARIANTS.LIGHT,
  style: INFO_POPOVER_ICON_STYLE,
  placement: InfoPopover.PLACEMENTS.TOP
};
const LABEL_VARIANTS = {
  SIDE: 'side',
  TOP: 'top'
};
/**
 * A FormField component which provides error message displaying.
 */
const FormField = /*#__PURE__*/React.forwardRef((_ref, ref) => {
  let {
      className,
      style,
      error,
      errorVariant = FORM_FIELD_ERROR_VARIANTS.CAPTION,
      label,
      labelVariant = LABEL_VARIANTS.SIDE,
      required,
      children,
      renderInfoPopover
    } = _ref,
    props = _objectWithoutProperties(_ref, _excluded);
  const id = useInstanceId();
  const labelId = "FormFieldLabel".concat(id);
  const errorId = "FormFieldError".concat(label);
  return /*#__PURE__*/React.createElement(_StyledDiv, _extends({}, props, {
    ref: ref,
    className: className,
    $_css: [{
      display: 'flex',
      width: '100%'
    }, variantSwitch({
      statement: labelVariant,
      variants: {
        [LABEL_VARIANTS.TOP]: {
          flexDirection: 'column'
        }
      }
    }), style]
  }), label && /*#__PURE__*/React.createElement(_StyledLabel, {
    id: labelId,
    "data-testid": "FormField__label",
    $_css2: [{
      display: 'flex',
      color: theme.tokens.colors.text.secondary.value
    }, variantSwitch({
      statement: labelVariant,
      variants: {
        [LABEL_VARIANTS.SIDE]: _objectSpread({
          marginRight: theme.spacing.XXS,
          marginTop: theme.spacing.XS
        }, theme.typography.labelMedium),
        [LABEL_VARIANTS.TOP]: _objectSpread({
          marginBottom: theme.spacing.XS
        }, theme.typography.labelSmall)
      }
    })]
  }, /*#__PURE__*/React.createElement("span", null, label), required && /*#__PURE__*/React.createElement(_StyledSpan, {
    $_css3: [{
      color: theme.tokens.colors.icon.error.value,
      marginLeft: '3px'
    }]
  }, "*"), renderInfoPopover === null || renderInfoPopover === void 0 ? void 0 : renderInfoPopover(DEFAULT_INFO_POPOVER_PROPS)), /*#__PURE__*/React.createElement(_StyledDiv2, {
    $_css4: [{
      display: 'flex',
      width: '100%',
      '&>*': {
        flex: '1 1 auto'
      }
    }, variantSwitch({
      statement: errorVariant,
      variants: {
        [FORM_FIELD_ERROR_VARIANTS.CAPTION]: {
          flexDirection: 'column'
        },
        [FORM_FIELD_ERROR_VARIANTS.TOOLTIP]: {
          alignItems: 'center'
        }
      }
    })]
  }, /*#__PURE__*/React.createElement("div", null, /*#__PURE__*/React.isValidElement(children) ? /*#__PURE__*/React.cloneElement(children, {
    'aria-labelledby': labelId,
    'aria-invalid': error ? true : undefined,
    'aria-errormessage': error ? errorId : undefined,
    'aria-required': required ? true : undefined,
    required
  }) : children), error && errorVariant === FORM_FIELD_ERROR_VARIANTS.TOOLTIP && /*#__PURE__*/React.createElement(_StyledDiv3, {
    id: errorId,
    role: "alert",
    "data-testid": "FormField__toolTip",
    $_css5: [{
      color: theme.tokens.colors.icon.error.value,
      flex: '0 0 auto',
      marginLeft: theme.spacing.XXS
    }]
  }, /*#__PURE__*/React.createElement(Tooltip, {
    content: error,
    placement: Tooltip.PLACEMENTS.TOP
  }, /*#__PURE__*/React.createElement(_AlertWarningFilledIcon, {
    size: 16
  }))), error && errorVariant === FORM_FIELD_ERROR_VARIANTS.CAPTION && /*#__PURE__*/React.createElement(_StyledDiv4, {
    id: errorId,
    role: "alert",
    "data-testid": "FormField__error",
    $_css6: [{
      display: 'flex',
      margin: "".concat(theme.spacing.XXS, "px 0")
    }]
  }, /*#__PURE__*/React.createElement(_AlertWarningFilledIcon, {
    size: 16,
    style: {
      flexShrink: 0,
      marginTop: theme.spacing.XXXS,
      color: theme.tokens.colors.icon.error.value
    }
  }), /*#__PURE__*/React.createElement(_StyledSpan2, {
    $_css7: [{
      fontSize: 12,
      color: theme.tokens.colors.text.error.value,
      marginLeft: theme.spacing.XS
    }]
  }, error))));
});
FormField.displayName = 'FormField';
FormField.propTypes = {
  /** Child input component(s) */
  children: PropTypes.any,
  /** The error message to display */
  error: PropTypes.any,
  /** Styles applied to the root element */
  style: stylePropType,
  /** Class applied to the root element */
  className: PropTypes.string,
  /** The error variant */
  errorVariant: PropTypes.oneOf(Object.values(FORM_FIELD_ERROR_VARIANTS)),
  /** The text for the label.  This text is also used to set the aria-label prop on the provided children */
  label: PropTypes.any,
  /** The label variant */
  labelVariant: PropTypes.oneOf(Object.values(LABEL_VARIANTS)),
  /** Field is required */
  required: PropTypes.bool,
  /** Render function for info popover that receives default props and renders InfoPopover */
  renderInfoPopover: PropTypes.func
};
export default Object.assign(FormField, {
  ERROR_VARIANTS: FORM_FIELD_ERROR_VARIANTS,
  LABEL_VARIANTS,
  INFO_POPOVER_VARIANTS: InfoPopover.VARIANTS
});
var _StyledDiv = _styled("div")(_templateObject || (_templateObject = _taggedTemplateLiteral(["", ""])), p => p.$_css);
var _StyledLabel = _styled2("label")(_templateObject2 || (_templateObject2 = _taggedTemplateLiteral(["", ""])), p => p.$_css2);
var _StyledSpan = _styled3("span")(_templateObject3 || (_templateObject3 = _taggedTemplateLiteral(["", ""])), p => p.$_css3);
var _StyledDiv2 = _styled4("div")(_templateObject4 || (_templateObject4 = _taggedTemplateLiteral(["", ""])), p => p.$_css4);
var _StyledDiv3 = _styled5("div")(_templateObject5 || (_templateObject5 = _taggedTemplateLiteral(["", ""])), p => p.$_css5);
var _StyledDiv4 = _styled6("div")(_templateObject6 || (_templateObject6 = _taggedTemplateLiteral(["", ""])), p => p.$_css6);
var _StyledSpan2 = _styled7("span").withConfig({
  displayName: "FormField___StyledSpan2",
  componentId: "sc-1f303vs-0"
})(["", ""], p => p.$_css7);
//# sourceMappingURL=FormField.js.map